import { Pencil, Trash } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Image1 from "@stackje/shared-components/cards/image/Image1";
import Image2 from "@stackje/shared-components/cards/image/Image2";
import Image3 from "@stackje/shared-components/cards/image/Image3";
import Image4 from "@stackje/shared-components/cards/image/Image4";
import Image5 from "@stackje/shared-components/cards/image/Image5";
import Image from "@stackje/shared-components/Image";
import { Button } from "@stackje/shared-components/ui/button";
import { ClearableInput } from "@stackje/shared-components/ui/clearable-fields";
import { SimpleMarkdownEditor } from "@stackje/shared-components/ui/simple-markdown-editor";
import {
  Dialog,
  DialogTrigger,
} from "@stackje/shared-components/ui/dialog";
import { Label } from "@stackje/shared-components/ui/label";
import { type CommonColor } from "@stackje/common/config";
import { ImageEditFormDialogContent } from "./image-edit-form-dialog-content";
import { useColorSchemeShuffler } from "../../../hooks/useColorSchemeShuffler";
import { sampleData } from "@/components/add-card/constants";

// Zod schema for validation
const imageCardFormSchema = z.object({
  title: z.string(),
  description: z.string(),
  images: z.array(z.object({
    image: z.object({
      src: z.string(),
      alt: z.string().optional(),
    }),
    caption: z.string().optional(),
  })),
  type: z.string(),
  colors: z.object({
    background: z.string(),
    foreground: z.string(),
    cardBackground: z.string(),
    cardForeground: z.string(),
    buttonBackground: z.string(),
  }),
});

export type ImageCardFormData = z.infer<typeof imageCardFormSchema>;

interface ImageCardFormProps {
  initialData: ImageCardFormData;
  mode: "add" | "edit";
  onTypeChange?: (type: string) => void;
}

export function useImageCardForm({
  initialData,
  mode,
  onTypeChange,
}: ImageCardFormProps) {
  const form = useForm<ImageCardFormData>({
    resolver: zodResolver(imageCardFormSchema),
    defaultValues: initialData,
  });

  const { fields, append, update, remove } = useFieldArray({
    control: form.control,
    name: "images",
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingImageIndex, setEditingImageIndex] = useState<number | null>(null);
  const [editingImageData, setEditingImageData] = useState<{
    image: { src: string; alt?: string };
    caption?: string;
  } | undefined>(undefined);
  const { colors, setColors, shuffleColors } = useColorSchemeShuffler(initialData.colors as CommonColor)

  // Watch form values for preview updates
  const watchedValues = form.watch();
  const title = watchedValues.title;
  const description = watchedValues.description;
  const images = watchedValues.images;
  const imageType = watchedValues.type;
  const isDirty = form.formState.isDirty;

  useEffect(() => {
    setColors(initialData.colors as CommonColor);
    form.setValue("colors", initialData.colors);
  }, [initialData.colors, setColors, form]);

  // Update form colors when shuffled
  useEffect(() => {
    form.setValue("colors", colors);
  }, [colors, form]);
  
  const toggleType = (newType?: string) => {
    if (newType) {
      form.setValue("type", newType);
      onTypeChange?.(newType);
      return;
    }

    const imageTypes = ["image3", "image4", "image5"];
    const currentIndex = imageTypes.indexOf(imageType);
    const nextIndex = (currentIndex + 1) % imageTypes.length;
    const nextType = imageTypes[nextIndex];
    form.setValue("type", nextType);
    onTypeChange?.(nextType);
  };

  const handleImageSubmit = (data: {
    image: { src: string; alt?: string };
    caption?: string;
  }) => {
    if (editingImageIndex !== null) {
      // Edit existing image
      update(editingImageIndex, data);
    } else {
      // Add new image
      append(data);
    }
    // Reset form
    setEditingImageIndex(null);
    setEditingImageData(undefined);
    setDialogOpen(false);
  };

  const handleOpenChangeDialog = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setEditingImageIndex(null);
      setEditingImageData(undefined);
    }
  };

  const formContent = (
    <>
      <div className="space-y-2" data-testid="image-form-content">
        <div className="py-2">
          <h2 className="text-lg font-semibold">Images Card</h2>
          <p className="text-sm text-muted-foreground">
            Not all fields will be used for a given card type.
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="text-red-500">*</span> indicates required fields
          </p>
        </div>

        <div className="border-t pb-2 pt-4">
          <Label htmlFor="title">Title</Label>
          <Controller
            name="title"
            control={form.control}
            render={({ field }) => (
              <ClearableInput
                id="title"
                value={field.value}
                onChange={field.onChange}
                placeholder="Enter title"
              />
            )}
          />
          <div className="my-2">
            <Label htmlFor="description">Description</Label>
            <Controller
              name="description"
              control={form.control}
              render={({ field }) => (
                <SimpleMarkdownEditor
                  id="description"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter description"
                />
              )}
            />
          </div>
        </div>

        <div className="border-t pb-2 pt-4">
          <h3 className="mb-2">Images</h3>
          {fields.map((field, index) => (
            <div
              key={index}
              className="space-y-2 border-2 shadow-sm px-4 py-2 rounded-md mb-4 hover:bg-gray-50 flex justify-between items-center"
            >
              <div className="flex items-center space-x-3 flex-1">
                {field.image.src && (
                  <div className="flex-shrink-0">
                    <Image
                      src={field.image.src}
                      alt={field.image.alt || field.caption || `Image ${index + 1}`}
                      width={48}
                      height={48}
                      className="object-cover rounded-md"
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {field.caption || `Image ${index + 1}`}
                  </p>
                  {field.image.alt && (
                    <p className="text-xs text-gray-500 truncate">Alt: {field.image.alt}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Edit image"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setEditingImageIndex(index);
                    setEditingImageData({
                      image: field.image,
                      caption: field.caption,
                    });
                    setDialogOpen(true);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Delete image"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    remove(index);
                  }}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          <div className="">
            <Dialog open={dialogOpen} onOpenChange={handleOpenChangeDialog}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setEditingImageIndex(null);
                    setEditingImageData(undefined);
                  }}
                >
                  Add an image
                </Button>
              </DialogTrigger>
              <ImageEditFormDialogContent
                key={`image-dialog-${editingImageIndex ?? 'new'}`}
                isEditing={editingImageIndex !== null}
                initialData={editingImageData}
                onSubmit={handleImageSubmit}
                editingImageIndex={editingImageIndex}
                imagesLength={fields.length}
                onClose={() => setDialogOpen(false)}
              />
            </Dialog>
          </div>
        </div>

        {/* Hidden inputs */}
        <input type="hidden" name="type" value={imageType} />
        <input type="hidden" name="colors" value={JSON.stringify(colors)} />
        {fields.map((field, index) => (
          <div key={field.id}>
            <input
              type="hidden"
              name={`imageSrc${index}`}
              value={field.image.src}
            />
            <input
              type="hidden"
              name={`imageAlt${index}`}
              value={field.image.alt || ""}
            />
            <input
              type="hidden"
              name={`imageCaption${index}`}
              value={field.caption || ""}
            />
          </div>
        ))}
      </div>
    </>
  );

  return {
    form,
    title,
    description,
    images,
    imageType,
    toggleType,
    shuffleColors,
    isDirty,
    formContent,
    handleSubmit: form.handleSubmit,
    previewContent: (() => {
      if (isDirty || mode === "edit") {
        const commonProps = {
          title,
          description,
          images,
          colors,
        };

        switch (imageType) {
          case "image1":
            return <Image1 {...commonProps} />;
          case "image2":
            return <Image2 {...commonProps} />;
          case "image3":
            return <Image3 {...commonProps} />;
          case "image4":
            return <Image4 {...commonProps} />;
          case "image5":
            return <Image5 {...commonProps} />;
          default:
            return <Image1 {...commonProps} />;
        }
      } else {
        const sampleProps = {
          title: sampleData.images?.title || "Sample Images",
          description: sampleData.images?.description || "Sample image gallery",
          images: sampleData.images?.images || [
            {
              image: { src: "https://via.placeholder.com/400", alt: "Sample image" },
              caption: "Sample caption"
            }
          ],
          colors,
        };

        switch (imageType) {
          case "image1":
            return <Image1 {...sampleProps} />;
          case "image2":
            return <Image2 {...sampleProps} />;
          case "image3":
            return <Image3 {...sampleProps} />;
          case "image4":
            return <Image4 {...sampleProps} />;
          case "image5":
            return <Image5 {...sampleProps} />;
          default:
            return <Image1 {...sampleProps} />;
        }
      }
    })(),
  };
}